<template>
  <div>
    <SidebarSection
      ref="section"
      class="mt-10"
      title="Gruppeneinkauf"
      :collapsedOnMount="gruppeneinkaeufe.length === 0"
    >
      <template #actions>
        <b-button
          v-if="!gruppenEinkaufExists"
          variant="outline-primary"
          size="sm"
          class="hinzufuegen-btn"
          @click="addGruppeneinkauf"
        >
          <Plus :size="16" />
          Gruppeneinkauf hinzufügen
        </b-button>
      </template>
      <div v-if="gruppeneinkaeufe.length > 0">
        <SidebarListCard
          v-for="(grp, i) in gruppeneinkaeufe"
          :key="grp.id"
          :index="i + 1"
          title="Gruppeneinkauf"
          deletable
          deleteTitle="Gruppeneinkauf löschen"
          @delete="deleteGrpFlughafen(grp.id)"
        >
          <SidebarFieldGrid>
            <SidebarFieldRow
              label="GRP Flughafen"
              :column="1"
              :changed="isGruppeneinkaufFieldChanged(grp, 'flughafenIataCode')"
            >
              <AirportsSucheMultiselect
                class="w-100"
                v-model="grp.flughafenIataCode"
                :isMultipleAllowed="false"
                :openDirection="'top'"
                :filterCountry="reisetermin ? reisetermin.reise.reisekuerzel.substring(0, 2) : ''"
                :airportBlacklist="airportBlacklistFor(grp)"
                height="32px"
              ></AirportsSucheMultiselect>
            </SidebarFieldRow>
            <SidebarFieldRow
              label="GRP PNR"
              :column="2"
              :changed="isGruppeneinkaufFieldChanged(grp, 'gruppePnr')"
            >
              <b-form-input
                v-model="grp.gruppePnr"
                class="grp__input"
                type="text"
                placeholder="-"
              ></b-form-input>
            </SidebarFieldRow>
            <SidebarFieldRow
              label="Eingekaufte Pax"
              :column="1"
              :changed="isGruppeneinkaufFieldChanged(grp, 'eingekauftePax')"
            >
              <b-form-input
                v-model="grp.eingekauftePax"
                class="grp__input"
                type="text"
                placeholder="-"
              ></b-form-input>
            </SidebarFieldRow>
            <SidebarFieldRow
              label="OB / IB Class"
              :column="2"
              split
              :changed="
                isGruppeneinkaufFieldChanged(grp, 'obClass') || isGruppeneinkaufFieldChanged(grp, 'ibClass')
              "
            >
              <b-form-input
                v-model="grp.obClass"
                class="grp__input"
                type="text"
                placeholder="-"
              ></b-form-input>
              <b-form-input
                v-model="grp.ibClass"
                class="grp__input"
                type="text"
                placeholder="-"
              ></b-form-input>
            </SidebarFieldRow>
            <SidebarFieldRow
              label="Flugnr. GRP Hin / Rück"
              :column="1"
              split
              :changed="
                isGruppeneinkaufFieldChanged(grp, 'flugnummerGruppeHin') ||
                isGruppeneinkaufFieldChanged(grp, 'flugnummerGruppeRueck')
              "
            >
              <b-form-input
                v-model="grp.flugnummerGruppeHin"
                class="grp__input"
                type="text"
                placeholder="-"
              ></b-form-input>
              <b-form-input
                v-model="grp.flugnummerGruppeRueck"
                class="grp__input"
                type="text"
                placeholder="-"
              ></b-form-input>
            </SidebarFieldRow>
            <SidebarFieldRow
              v-for="preisfeld in preisfelder"
              :key="preisfeld.feld"
              :label="preisfeld.label"
              :column="preisfeld.column"
              :changed="isGruppeneinkaufFieldChanged(grp, preisfeld.feld)"
            >
              <!-- Das label umschließt Eingabe und Einheit, damit ein Klick auf das € die Eingabe fokussiert -->
              <label class="grp__affix">
                <b-form-input
                  v-model="grp[preisfeld.feld]"
                  class="grp__input--affix"
                  type="number"
                  placeholder="-"
                ></b-form-input>
                <span class="grp__append">€</span>
              </label>
            </SidebarFieldRow>
            <SidebarFieldRow label="Gesamtpreis GRP" :column="1" readonly>
              {{
                Number.isFinite(grp.gesamtpreisGruppeneinkauf)
                  ? (Math.round(grp.gesamtpreisGruppeneinkauf * 100) / 100).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) + '€'
                  : '-'
              }}
            </SidebarFieldRow>
            <SidebarFieldRow :column="2" />
          </SidebarFieldGrid>
        </SidebarListCard>
      </div>
      <div v-else class="grp-empty">Noch keine Gruppeneinkäufe angelegt.</div>
    </SidebarSection>

    <DeleteConfirmationModal
      id="modalGrpFlughafenLoeschen"
      title="GRP Flughafen löschen?"
      entityBezeichnung="den GRP Flughafen"
      @confirm="confirmDeleteGrpFlughafen"
    />
  </div>
</template>

<script>
import { Plus } from 'lucide-vue';
import apiService from '@/core/common/services/api.service';
import AirportsSucheMultiselect from '@/components/common/airports-suche-multiselect.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import SidebarSection from '@/components/common/sidebar-section/sidebar-section.vue';
import SidebarListCard from '@/components/common/sidebar-section/sidebar-list-card.vue';
import SidebarFieldGrid from '@/components/common/sidebar-section/sidebar-field-grid.vue';
import SidebarFieldRow from '@/components/common/sidebar-section/sidebar-field-row.vue';

const TEMP_GRPFlughafen_ID = -1;

export default {
  name: 'SidebarFlugplanungGruppeneinkaeufe',
  components: {
    Plus,
    AirportsSucheMultiselect,
    DeleteConfirmationModal,
    SidebarSection,
    SidebarListCard,
    SidebarFieldGrid,
    SidebarFieldRow,
  },
  props: {
    // Der aktive Reisetermin liefert id, reiseterminkuerzel und das Zielland für den Flughafenfilter
    reisetermin: { type: Object, default: null },
  },
  data() {
    return {
      // Preisfelder mit Euro-Einheit in Anzeigereihenfolge
      preisfelder: [
        { feld: 'hauptstreckePreisNetto', label: 'Preis Netto', column: 2 },
        { feld: 'inlandsflugPreis', label: 'INFL Netto', column: 1 },
        { feld: 'taxesFlug', label: 'Taxes Gesamt', column: 2 },
      ],
      // Lokale kopie der Gruppeneinkäufe
      gruppeneinkaeufe: [],
      pendingDeleteGrpFlughafenId: null,
    };
  },
  watch: {
    // Wechselt der Reisetermin, wird die kopie neu aus den geladenen Daten aufgebaut
    reisetermin: {
      immediate: true,
      handler() {
        this.syncFromReisetermin();
        if (this.gruppeneinkaeufe.length > 0) {
          this.$refs.section?.open();
        } else {
          this.$refs.section?.close();
        }
      },
    },
    // Änderungen an der kopie melden den Speicherzustand
    gruppeneinkaeufe: {
      deep: true,
      handler() {
        this.emitChanged();
      },
    },
    // Ändert sich die führende Flugnummer, wird der Airline-Bezug im Container ausgelöst
    flugnummerGruppeHin(newValue, oldValue) {
      this.$emit('leitflugnummer-changed', { newValue, oldValue });
    },
  },
  computed: {
    // Führende Flugnummer Hin
    flugnummerGruppeHin() {
      if (!this.gruppeneinkaeufe || this.gruppeneinkaeufe.length === 0) {
        return null;
      }
      // Nur gespeicherte Einträge berücksichtigen, keine temporären
      const savedEntries = this.gruppeneinkaeufe.filter(grp => !grp.isTemporary);

      if (savedEntries.length > 0) {
        return savedEntries[0]?.flugnummerGruppeHin;
      }

      return this.gruppeneinkaeufe[0]?.flugnummerGruppeHin;
    },
    // Prüft, ob ein temporärer Gruppeneinkauf existiert -> steuert den +-Button
    gruppenEinkaufExists() {
      return this.gruppeneinkaeufe?.some(grp => grp?.isTemporary === true);
    },
    // Der aktuell in Bearbeitung befindliche temporäre Gruppeneinkauf
    temporaererGruppeneinkauf() {
      return this.gruppeneinkaeufe?.find(grp => grp?.isTemporary === true);
    },
    // Prüft, ob der temporäre Gruppeneinkauf alle Pflichtfelder befüllt hat
    readyToSaveGruppeneinkauf() {
      if (!this.temporaererGruppeneinkauf) return false;

      const grp = this.temporaererGruppeneinkauf;

      // flughafenIataCode ist das einzige Pflichtfeld
      return grp.flughafenIataCode !== null;
    },
    // Liefert alle geänderten, nicht-temporären und vollständig befüllten Gruppeneinkäufe
    gruppeneinkauefChanged() {
      return this.gruppeneinkaeufe.filter(grp => {
        if (grp.isTemporary || !grp.valuesBeforeEdit) return false;

        // Erst prüfen ob geändert
        const hasChanges =
          grp.flughafenIataCode !== grp.valuesBeforeEdit.flughafenIataCode ||
          grp.gruppePnr !== grp.valuesBeforeEdit.gruppePnr ||
          grp.eingekauftePax !== grp.valuesBeforeEdit.eingekauftePax ||
          grp.flugnummerGruppeHin !== grp.valuesBeforeEdit.flugnummerGruppeHin ||
          grp.flugnummerGruppeRueck !== grp.valuesBeforeEdit.flugnummerGruppeRueck ||
          grp.obClass !== grp.valuesBeforeEdit.obClass ||
          grp.ibClass !== grp.valuesBeforeEdit.ibClass ||
          grp.hauptstreckePreisNetto !== grp.valuesBeforeEdit.hauptstreckePreisNetto ||
          grp.inlandsflugPreis !== grp.valuesBeforeEdit.inlandsflugPreis ||
          grp.taxesFlug !== grp.valuesBeforeEdit.taxesFlug;

        if (!hasChanges) return false;

        // Nur GRP Flughafen ist Pflichtfeld
        const isComplete =
          grp.flughafenIataCode !== null &&
          grp.flughafenIataCode !== undefined &&
          grp.flughafenIataCode !== '';

        return isComplete;
      });
    },
    // Prüft ob sich ein Feld in der Gruppe geändert hat oder bei neuen Gruppen einen Wert hat
    isGruppeneinkaufFieldChanged() {
      return (gruppe, feldName) => {
        // Keine Gruppe = keine Änderung
        if (!gruppe) return false;

        // Bei neuen/temporären Gruppen: markiere als geändert wenn Wert vorhanden
        if (gruppe.isTemporary || !gruppe.valuesBeforeEdit) {
          const wert = gruppe[feldName];
          return wert !== null && wert !== undefined && wert !== '';
        }

        // Bei bestehenden Gruppen: vergleiche mit ursprünglichem Wert
        return gruppe[feldName] !== gruppe.valuesBeforeEdit[feldName];
      };
    },
  },
  mounted() {
    if (this.flugnummerGruppeHin != null) {
      this.$emit('leitflugnummer-changed', { newValue: this.flugnummerGruppeHin, oldValue: null });
    }
  },
  methods: {
    syncFromReisetermin() {
      const source = this.reisetermin?.flugeinkauf?.gruppeneinkaeufe ?? [];
      this.gruppeneinkaeufe = source.map(grp => {
        if (grp.isTemporary) {
          return { ...grp };
        }
        return {
          ...grp,
          valuesBeforeEdit: {
            flughafenIataCode: grp.flughafenIataCode,
            gruppePnr: grp.gruppePnr,
            eingekauftePax: grp.eingekauftePax,
            flugnummerGruppeHin: grp.flugnummerGruppeHin,
            flugnummerGruppeRueck: grp.flugnummerGruppeRueck,
            obClass: grp.obClass,
            ibClass: grp.ibClass,
            hauptstreckePreisNetto: grp.hauptstreckePreisNetto,
            inlandsflugPreis: grp.inlandsflugPreis,
            taxesFlug: grp.taxesFlug,
          },
        };
      });
    },
    emitChanged() {
      this.$emit('changed', {
        hasChanges: this.readyToSaveGruppeneinkauf || this.gruppeneinkauefChanged.length > 0,
        isValid: true,
      });
    },
    // Flughäfen der anderen Gruppeneinkäufe, damit ein Flughafen nicht mehrfach gewählt werden kann
    airportBlacklistFor(grp) {
      return this.gruppeneinkaeufe.filter(g => g.id !== grp.id).map(g => g.flughafenIataCode);
    },
    // Zeigt die leere Hülle des Gruppeneinkaufes in der UI an
    addGruppeneinkauf() {
      this.$refs.section.open();
      if (!this.gruppenEinkaufExists) {
        this.gruppeneinkaeufe.unshift({
          isOnlyLocal: true,
          isTemporary: true,
          id: TEMP_GRPFlughafen_ID,
          flughafenIataCode: null,
          gruppePnr: null,
          eingekauftePax: null,
          flugnummerGruppeHin: null,
          flugnummerGruppeRueck: null,
          obClass: null,
          ibClass: null,
          hauptstreckePreisNetto: null,
          inlandsflugPreis: null,
          taxesFlug: null,
          valuesBeforeEdit: {
            flughafenIataCode: null,
            gruppePnr: null,
            eingekauftePax: null,
            flugnummerGruppeHin: null,
            flugnummerGruppeRueck: null,
            obClass: null,
            ibClass: null,
            hauptstreckePreisNetto: null,
            inlandsflugPreis: null,
            taxesFlug: null,
          },
        });
      }
    },
    // Startet das Löschen eines Gruppeneinkaufs
    deleteGrpFlughafen(id) {
      // wenn es nur ein temporärer ist, entferne ihn
      if (id === TEMP_GRPFlughafen_ID) {
        this.gruppeneinkaeufe = this.gruppeneinkaeufe.filter(grp => grp.id !== id);
        return;
      }

      this.pendingDeleteGrpFlughafenId = id;
      this.$bvModal.show('modalGrpFlughafenLoeschen');
    },
    // Löscht den bestätigten Gruppeneinkauf per API und entfernt ihn
    confirmDeleteGrpFlughafen() {
      this.$bvModal.hide('modalGrpFlughafenLoeschen');
      const id = this.pendingDeleteGrpFlughafenId;
      if (id === null) return;

      this.$emit('loading', true);

      // Löscht über den Flughafen-Code statt über die Gruppeneinkauf-ID
      const grpToDelete = this.gruppeneinkaeufe.find(grp => grp.id === id);
      apiService
        .post('/Reisetermin/bulkDeleteGruppeneinkauf', {
          reiseterminIds: [this.reisetermin?.id],
          flughafenIataCode: grpToDelete?.flughafenIataCode,
        })
        .then(() => {
          this.toast('Erfolgreich gelöscht');
          this.gruppeneinkaeufe = this.gruppeneinkaeufe.filter(grp => grp.id !== id);
          this.$emit('deletedGrpFlughafen', {
            reiseterminkuerzel: this.reisetermin.reiseterminkuerzel,
            id,
          });
        })
        .finally(() => {
          this.$emit('loading', false);
          this.pendingDeleteGrpFlughafenId = null;
        });
    },
    // Speichermethode
    async save() {
      // Gruppeneinkauf speichern wenn bereit
      if (this.readyToSaveGruppeneinkauf) {
        await this.saveGruppeneinkauf();
      }

      // Geänderte Gruppeneinkäufe speichern
      if (this.gruppeneinkauefChanged.length > 0) {
        let changedCount = 0;
        for (const grp of this.gruppeneinkauefChanged) {
          await this.saveChangedGruppeneinkauf(grp);
          changedCount++;
        }
        this.toast(`${changedCount} Gruppeneinkauf${changedCount > 1 ? 'e' : ''} erfolgreich aktualisiert.`);
      }
    },
    // Legt den temporären Gruppeneinkauf per API neu an
    async saveGruppeneinkauf() {
      const grp = this.temporaererGruppeneinkauf;
      if (!grp) return;

      const params = {
        flughafenIataCode: grp.flughafenIataCode,
        gruppePnr: grp.gruppePnr,
        eingekauftePax: Number(grp.eingekauftePax),
        flugnummerGruppeHin: grp.flugnummerGruppeHin,
        flugnummerGruppeRueck: grp.flugnummerGruppeRueck,
        obClass: grp.obClass,
        ibClass: grp.ibClass,
        hauptstreckePreisNetto: Number(grp.hauptstreckePreisNetto),
        inlandsflugPreis: Number(grp.inlandsflugPreis),
        taxesFlug: Number(grp.taxesFlug),
      };

      try {
        // Für die Sidebar wird nur ein einzelner Termin übergeben (Array mit einer ID)
        const response = await apiService.post('/Reisetermin/bulkCreateGruppeneinkauf', {
          reiseterminIds: [this.reisetermin?.id],
          gruppeneinkaufDto: params,
        });
        const newGruppeneinkauf = response.data.result;

        // Entferne temporären Gruppeneinkauf
        this.gruppeneinkaeufe = this.gruppeneinkaeufe.filter(grp => !grp?.isTemporary);

        // valuesBeforeEdit für neuen Gruppeneinkauf setzen
        newGruppeneinkauf.valuesBeforeEdit = {
          flughafenIataCode: newGruppeneinkauf.flughafenIataCode,
          gruppePnr: newGruppeneinkauf.gruppePnr,
          eingekauftePax: newGruppeneinkauf.eingekauftePax,
          flugnummerGruppeHin: newGruppeneinkauf.flugnummerGruppeHin,
          flugnummerGruppeRueck: newGruppeneinkauf.flugnummerGruppeRueck,
          obClass: newGruppeneinkauf.obClass,
          ibClass: newGruppeneinkauf.ibClass,
          hauptstreckePreisNetto: newGruppeneinkauf.hauptstreckePreisNetto,
          inlandsflugPreis: newGruppeneinkauf.inlandsflugPreis,
          taxesFlug: newGruppeneinkauf.taxesFlug,
        };

        this.gruppeneinkaeufe.push(newGruppeneinkauf);

        this.toast('Gruppeneinkauf erfolgreich gespeichert.');

        this.$emit('createdGruppeneinkauf', {
          reiseterminkuerzel: this.reisetermin.reiseterminkuerzel,
          newGruppeneinkauf,
        });
        this.$emit('newGruppeneinkauf', newGruppeneinkauf);
      } catch (error) {
        // Entferne temporären Gruppeneinkauf im Fehlerfall
        this.gruppeneinkaeufe = this.gruppeneinkaeufe.filter(grp => !grp?.isTemporary);
        throw error;
      }
    },
    // Speichert einen bereits bestehenden, geänderten Gruppeneinkauf
    async saveChangedGruppeneinkauf(grp) {
      const params = {
        flughafenIataCode: grp.flughafenIataCode,
        gruppePnr: grp.gruppePnr,
        eingekauftePax: Number(grp.eingekauftePax),
        flugnummerGruppeHin: grp.flugnummerGruppeHin,
        flugnummerGruppeRueck: grp.flugnummerGruppeRueck,
        obClass: grp.obClass,
        ibClass: grp.ibClass,
        hauptstreckePreisNetto: Number(grp.hauptstreckePreisNetto),
        inlandsflugPreis: Number(grp.inlandsflugPreis),
        taxesFlug: Number(grp.taxesFlug),
      };

      // Identifiziert den Gruppeneinkauf über den flughafenIataCode statt über die ID
      const response = await apiService.put('/Reisetermin/bulkUpdateGruppeneinkauf', {
        reiseterminIds: [this.reisetermin?.id],
        gruppeneinkaufDto: params,
      });

      // Update valuesBeforeEdit nach erfolgreichem Speichern
      grp.valuesBeforeEdit = {
        flughafenIataCode: grp.flughafenIataCode,
        gruppePnr: grp.gruppePnr,
        eingekauftePax: grp.eingekauftePax,
        flugnummerGruppeHin: grp.flugnummerGruppeHin,
        flugnummerGruppeRueck: grp.flugnummerGruppeRueck,
        obClass: grp.obClass,
        ibClass: grp.ibClass,
        hauptstreckePreisNetto: grp.hauptstreckePreisNetto,
        inlandsflugPreis: grp.inlandsflugPreis,
        taxesFlug: grp.taxesFlug,
      };

      this.$emit('updatedGruppeneinkauf', {
        reiseterminkuerzel: this.reisetermin.reiseterminkuerzel,
        gruppeneinkauf: grp,
      });

      return response;
    },
  },
};
</script>

<style scoped>
.hinzufuegen-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.grp__input {
  height: var(--control-h-sm);
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--radius-sm);
}

/* Rahmen um Eingabe und €-Einheit, optisch wie ein einzelnes Eingabefeld */
.grp__affix {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: var(--control-h-sm);
  margin: 0;
  padding: 0 8px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: text;
}

.grp__affix:focus-within {
  border-color: var(--primary);
  box-shadow: var(--focus-ring);
}

.grp__input--affix {
  height: auto;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  border: 0;
  box-shadow: none;
  -moz-appearance: textfield;
}

.grp__input--affix:focus {
  box-shadow: none;
}

.grp__input--affix::-webkit-outer-spin-button,
.grp__input--affix::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.grp__append {
  font-size: 13px;
  color: var(--text-muted);
}

.grp-empty {
  padding: 24px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted);
}
</style>
